<template>
  <div>
    <AppTableHeader>
      <template #left>
        {{
          $t(
            "horses:vaccinations.vaccinationsChecksHistory.heading",
            "Vaccinations checks history"
          )
        }}
      </template>
      <template #right>
        <AppChip color="accent">{{ checks.length }}</AppChip>
        <span v-if="lastVaccinationCheckDate" class="ml-3">
          {{ $dayjs.date(lastVaccinationCheckDate) }}</span
        >
      </template>
    </AppTableHeader>
    <AppTable
      :headers="headers"
      :items="checks"
      :hide-headers="$vuetify.display.smAndDown"
      :loading="loading"
    >
      <template #no-data>
        {{
          $t(
            "horses:vaccinations.vaccinationsChecksHistory.noRecords.text",
            "No vaccination checks recorded for this horse."
          )
        }}
      </template>
      <template #items>
        <tr v-for="check in checks" :key="check.id">
          <td>
            <div class="hidden-sm-and-down">
              {{ $dayjs.date(check.checkDate) }}
            </div>
            <div class="hidden-md-and-up">
              <div class="mb-2">
                <div class="mb-1">
                  <b class="text-primary" style="font-size: 14px">{{
                    $t(
                      "horses:vaccinations.vaccinationsChecksHistory.date.label",
                      "Date"
                    )
                  }}</b>
                </div>
                {{ $dayjs.date(check.checkDate) }}
              </div>

              <div class="mb-2">
                <div class="mb-1">
                  <b class="text-primary" style="font-size: 14px">{{
                    $t(
                      "horses:vaccinations.vaccinationsChecksHistory.event.label",
                      "Event"
                    )
                  }}</b>
                </div>
                <AppShowEvent :event="check.event" />
              </div>
            </div>
          </td>
          <td class="hidden-sm-and-down">
            <AppShowEvent :event="check.event" />
          </td>
          <td style="vertical-align: top" class="text-end">
            <v-btn-icon
              v-if="
                $allowedMutation(
                  check.allowedMutations,
                  'vaccinationCheckDelete'
                )
              "
              class="ma-2"
              color="remove"
              data-test="vaccinationCheckDelete"
              :aria-label="$t('common:deleteButton.alt')"
              icon
              size="small"
              @click="deleteCheck(check, $event)"
            >
              <v-icon color="white" size="small">$iconTrash</v-icon>
            </v-btn-icon>
          </td>
        </tr>
      </template>
    </AppTable>
  </div>
</template>
<script>
export default {
  name: "VaccinationChecks"
};
</script>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import { injectSnackbarError, injectAppConfirm } from "@composables/injections";
import { useQuery } from "@vue/apollo-composable";
import { useTranslation } from "i18next-vue";
import { apolloClient } from "@/plugins/apollo";
import { showLoadingButton } from "@/utils/showLoadingButton";
import { clone } from "lodash-es";
import dayjs from "dayjs";

import AppTableHeader from "@elements/table/AppTableHeader.vue";
import AppTable from "@elements/table/AppTable.vue";
import AppShowEvent from "@AppShows/event/AppShowEvent.vue";
import AppChip from "@elements/AppChip.vue";

const props = defineProps({
  horse: { type: Object, required: true }
});

const { t } = useTranslation();

const { appConfirm } = injectAppConfirm();
const { snackbarServerError } = injectSnackbarError();

const headers = computed(() => [
  {
    text: t("horses:vaccinations.vaccinationsChecksHistory.date.label", "Date")
  },
  {
    text: t(
      "horses:vaccinations.vaccinationsChecksHistory.event.label",
      "Event"
    )
  },
  { text: "" }
]);

const checks = ref([]);
const lastVaccinationCheckDate = ref(null);
const { onResult, loading } = useQuery(
  gql`
    query horseVaccinationChecks($id: ID!) {
      horse(id: $id) {
        id
        lastVaccinationCheckDate
        vaccinationChecks {
          id
          checkDate
          event {
            id
            ${AppShowEvent.fragments.event}
          }
          allowedMutations
        }
      }
    }
  `,
  () => ({ id: props.horse.id })
);

onResult(({ data, loading }) => {
  if (loading) return;
  checks.value = clone(data.horse.vaccinationChecks).sort((a, b) => {
    if (!a.checkDate) return 1;
    return dayjs(b.checkDate).isAfter(a.checkDate) ? 1 : -1;
  });
  lastVaccinationCheckDate.value = data.horse.lastVaccinationCheckDate;
});

async function deleteCheck(check, evt) {
  const response = await appConfirm(
    t(
      "horses:vaccinations.vaccinationsChecksHistory.deleteCheckRecord.heading",
      "Delete vaccinations' check record"
    ),
    t(
      "horses:vaccinations.vaccinationsChecksHistory.deleteCheckRecord.question",
      "You are going to delete the vaccinations'  check record. This action cannot be reversed and  horse’s vaccinations'  check record will be deleted. Are you sure?"
    )
  );
  if (!response) return;

  const removeLoading = showLoadingButton(evt.target);

  try {
    await apolloClient.mutate({
      mutation: gql`
        mutation vaccinationCheckDelete($id: ID!) {
          vaccinationCheckDelete(input: { id: $id }) {
            vaccinationCheck {
              id
            }
          }
        }
      `,
      variables: {
        id: check.id
      },
      awaitRefetchQueries: true,
      refetchQueries: ["horseVaccinationChecks"]
    });
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    removeLoading();
  }
}
</script>
